/**
 * Transit Time Service
 * Fetches real-time transit estimates from the Shopify App Proxy endpoint
 * which routes to the AWS Lambda TransitTimeLookup function.
 */

import type {
  ApiErrorResponse,
  ApiResponse,
  WarehouseResult,
} from "./types/apiTypes.js";

/** App Proxy endpoint for transit time estimates */
const TRANSIT_TIME_ENDPOINT = "/apps/lagniappeserver/shipping-estimate";

/** Default ZIP code fallback (Baton Rouge, LA) */
const DEFAULT_ZIP = "70806";

/** Maximum retry attempts for API calls */
const MAX_RETRIES = 3;

/** Retry delays in milliseconds */
const RETRY_DELAYS = [0, 200, 800];

/**
 * Abort a hung request rather than leave the page at "Loading..." indefinitely.
 *
 * Must exceed the SERVER's own deadline (UPS_TOTAL_DEADLINE_MS, 8s) so the server wins the race and
 * returns a shaped response with null estimates, which renders as "N/A". Aborting first would throw
 * away a usable answer. There was previously no timeout here at all, so a dropped connection hung
 * the product page forever.
 */
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Type guard to check if response is an error
 */
function isApiError(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === "object" && response !== null && "error" in response
  );
}

/**
 * Format an optional cart subtotal for the `subtotal` query parameter.
 *
 * The server accepts decimal dollars with at most two places and treats anything else as absent
 * (logging a warning, never erroring), so send a clean value or send nothing. Negative and
 * non-finite inputs are dropped here rather than round-tripped.
 *
 * @param subtotalCents - Post-discount merchandise subtotal in cents, or undefined
 * @returns Dollar string for the query param, or undefined to omit it
 */
function formatSubtotalParam(subtotalCents?: number): string | undefined {
  if (
    subtotalCents === undefined ||
    !Number.isFinite(subtotalCents) ||
    subtotalCents < 0
  ) {
    return undefined;
  }

  return (Math.round(subtotalCents) / 100).toFixed(2);
}

/**
 * Fetches transit time estimates from the API with retry logic
 *
 * @param warehouseCodes - Array of warehouse codes (e.g., ['WPBR', 'NLAL'])
 * @param destZip - Destination ZIP code (5 digits)
 * @param subtotalCents - Optional cart subtotal in CENTS. Supplying it makes the server return
 *   priced localDelivery / shipping / pickup quotes; omitting it returns dates only. The product
 *   page has no cart, so it omits this.
 * @returns Promise resolving to ApiResponse with transit estimates
 * @throws Error if all retry attempts fail
 */
export async function fetchTransitTimes(
  warehouseCodes: string[],
  destZip: string = DEFAULT_ZIP,
  subtotalCents?: number,
): Promise<ApiResponse> {
  // Validate inputs
  if (warehouseCodes.length === 0) {
    throw new Error("At least one warehouse code is required");
  }

  const cleanZip = destZip.trim().substring(0, 5);
  if (!/^\d{5}$/.test(cleanZip)) {
    console.warn(
      `Invalid ZIP code "${destZip}", using default: ${DEFAULT_ZIP}`,
    );
    destZip = DEFAULT_ZIP;
  } else {
    destZip = cleanZip;
  }

  // Build URL with query parameters
  const warehouseParam = warehouseCodes.join(",");
  const subtotalParam = formatSubtotalParam(subtotalCents);
  const url =
    `${TRANSIT_TIME_ENDPOINT}?warehouses=${encodeURIComponent(warehouseParam)}&dest_zip=${encodeURIComponent(destZip)}` +
    (subtotalParam === undefined
      ? ""
      : `&subtotal=${encodeURIComponent(subtotalParam)}`);

  // Fetch with retry logic
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    try {
      // Apply delay for retries
      if (attempt > 1) {
        await delay(RETRY_DELAYS[attempt - 1] ?? 800);
      }

      // A fresh controller per attempt — an AbortSignal is single-use, so reusing one would make
      // every retry after the first abort instantly.
      const controller = new AbortController();
      timeoutHandle = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      // Handle non-OK responses
      if (!response.ok) {
        // For 4xx/5xx, try to parse error body
        const errorBody = await parseResponseBody(response);

        if (isApiError(errorBody)) {
          throw new Error(
            `API Error: ${errorBody.error}${errorBody.details ? ` - ${errorBody.details}` : ""}`,
          );
        }

        // Retry on 5xx errors
        if (response.status >= 500 && attempt < MAX_RETRIES) {
          console.warn(
            `Transit API returned ${response.status}, retrying (attempt ${attempt}/${MAX_RETRIES})...`,
          );
          continue;
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse successful response
      const data = (await response.json()) as unknown;

      // Validate response structure
      if (!isValidApiResponse(data)) {
        throw new Error("Invalid API response structure");
      }

      return data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      // Log warning for non-final attempts
      if (attempt < MAX_RETRIES) {
        console.warn(
          `Transit API call failed (attempt ${attempt}/${MAX_RETRIES}): ${errorMessage}`,
        );
        continue;
      }

      // Final attempt failed
      console.error(
        `Transit API call failed after ${MAX_RETRIES} attempts: ${errorMessage}`,
      );
      throw error;
    } finally {
      // Cleared on every path, success included: a pending timer would abort nothing but keeps a
      // closure alive per attempt, and product pages call this on every variant change.
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error("Unexpected: exceeded retry loop");
}

/**
 * Type guard to validate API response structure
 */
function isValidApiResponse(data: unknown): data is ApiResponse {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.destZip === "string" &&
    Array.isArray(obj.results) &&
    obj.results.every(isValidWarehouseResult) &&
    typeof obj.enableLocalDelivery === "boolean" &&
    (obj.nextDeliveryDate === null || typeof obj.nextDeliveryDate === "string")
  );
}

/**
 * Type guard to validate warehouse result structure
 */
function isValidWarehouseResult(item: unknown): item is WarehouseResult {
  if (typeof item !== "object" || item === null) {
    return false;
  }

  const obj = item as Record<string, unknown>;

  return (
    typeof obj.warehouseCode === "string" &&
    (obj.orderByDT === null || typeof obj.orderByDT === "string") &&
    (obj.transitDays === null || typeof obj.transitDays === "number") &&
    (obj.estimatedShipDate === null ||
      typeof obj.estimatedShipDate === "string") &&
    (obj.estimatedDeliveryDate === null ||
      typeof obj.estimatedDeliveryDate === "string") &&
    (obj.totalDays === null || typeof obj.totalDays === "number")
  );
}

/**
 * Parse response body, handling both JSON and text responses
 */
async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    return response.json();
  }

  return response.text();
}

/**
 * Delay helper for retry logic
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Format a date string as "DDD MM/DD" (e.g., "Wed 12/05")
 *
 * @param dateString - ISO date string (e.g., "2025-12-05")
 * @returns Formatted date string or empty string if invalid
 */
export function formatDeliveryDate(dateString: string | null): string {
  if (!dateString) {
    return "";
  }

  try {
    const date = new Date(dateString + "T00:00:00");

    if (isNaN(date.getTime())) {
      return "";
    }

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = dayNames[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${dayOfWeek} ${month}/${day}`;
  } catch {
    return "";
  }
}

/**
 * Format an order-by datetime string based on relative date
 * - If date part is today: "{HH:MM AM/PM} today"
 * - If date part is tomorrow: "{HH:MM AM/PM} tomorrow"
 * - Otherwise: "{HH:MM AM/PM} on {DDD MM/DD}"
 *
 * @param orderByDT - ISO datetime string (e.g., "2025-12-03T14:00:00")
 * @returns Formatted string or empty string if invalid
 */
export function formatOrderByDateTime(orderByDT: string | null): string {
  if (!orderByDT) {
    return "";
  }

  try {
    const orderByDate = new Date(orderByDT);
    if (isNaN(orderByDate.getTime())) {
      return "";
    }

    // Format time as HH:MM AM/PM
    let hours = orderByDate.getHours();
    const minutes = String(orderByDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours; // 0 should be 12
    const timeStr = `${hours}:${minutes} ${ampm}`;

    // Get today and tomorrow dates (at midnight for comparison)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Get order-by date at midnight for comparison
    const orderByDateOnly = new Date(
      orderByDate.getFullYear(),
      orderByDate.getMonth(),
      orderByDate.getDate(),
    );

    if (orderByDateOnly.getTime() === today.getTime()) {
      return `${timeStr} today`;
    } else if (orderByDateOnly.getTime() === tomorrow.getTime()) {
      return `${timeStr} tomorrow`;
    } else {
      // Format as "HH:MM AM/PM on DDD MM/DD"
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayOfWeek = dayNames[orderByDate.getDay()];
      const month = String(orderByDate.getMonth() + 1).padStart(2, "0");
      const day = String(orderByDate.getDate()).padStart(2, "0");
      return `${timeStr} on ${dayOfWeek} ${month}/${day}`;
    }
  } catch {
    return "";
  }
}

// Re-export types for consumers
export type { ApiErrorResponse, ApiResponse, WarehouseResult };
export type {
  DeliveryWindow,
  LocalDeliveryQuote,
  LocalDeliveryZoneId,
  PickupQuote,
  ShippingQuote,
} from "./types/apiTypes.js";
